import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useLang, localName, localDesc } from "../i18n";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import { VerifiedBadge } from "../components/ChefCard";
import { Eyebrow, Reveal, GoldButton } from "../components/motion";
import { Star, MapPin, GraduationCap, Heart, Clock } from "lucide-react";
import { toast } from "sonner";

export default function ChefProfile() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: chef, isLoading, isError } = useQuery({
    queryKey: ["chef", slug],
    queryFn: () => api.get(`/chefs/slug/${slug}`).then((r) => r.data),
  });
  const { data: favorites } = useQuery({
    queryKey: ["favorites"],
    queryFn: () => api.get("/favorites").then((r) => r.data),
    enabled: !!user,
  });
  const toggleFav = useMutation({
    mutationFn: () => api.post("/favorites/toggle", { type: "chef", item_id: chef.id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["favorites"] }),
  });

  if (isLoading) return <div className="pt-40 pb-40 text-center text-neutral-500">{t("common.loading")}</div>;
  if (isError || !chef) return <div className="pt-40 pb-40 text-center text-neutral-400">{t("common.error")}</div>;

  const isFav = favorites?.chef_ids?.includes(chef.id);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: chef.name,
    jobTitle: "Private Chef",
    image: chef.photo,
    address: { "@type": "PostalAddress", addressLocality: chef.city },
  };

  return (
    <div className="pt-[72px]">
      <Seo title={`${chef.name} — ${t("common.verified")} | Chef à Domicile`} description={chef.bio?.slice(0, 155)} jsonLd={jsonLd} image={chef.photo} />

      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 py-16 md:py-24 grid lg:grid-cols-5 gap-12">
        <Reveal className="lg:col-span-2">
          <div className="relative">
            <img src={chef.photo} alt={chef.name} className="w-full aspect-[4/5] object-cover" />
            <div className="absolute top-5 start-5"><VerifiedBadge /></div>
            {user && (
              <button data-testid="favorite-toggle-btn" onClick={() => toggleFav.mutate()}
                className={`absolute top-5 end-5 p-2.5 bg-black/60 border transition-colors ${isFav ? "border-gold text-gold" : "border-white/20 text-white hover:text-gold"}`}>
                <Heart size={18} className={isFav ? "fill-current" : ""} />
              </button>
            )}
          </div>
        </Reveal>
        <div className="lg:col-span-3">
          <Reveal>
            <Eyebrow>{chef.cuisines?.map((c) => localName(c, lang)).join(" · ")}</Eyebrow>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory mb-5" data-testid="chef-profile-name">{chef.name}</h1>
            <div className="flex flex-wrap items-center gap-5 text-sm text-neutral-400 mb-8">
              <span className="flex items-center gap-1.5"><MapPin size={14} className="text-gold" /> {chef.city}</span>
              <span className="flex items-center gap-1.5"><Star size={14} className="text-gold fill-gold" /> {Number(chef.rating).toFixed(1)} ({chef.review_count} {t("common.reviews")})</span>
              <span>{t("common.yearsExp", { n: chef.years_experience })}</span>
            </div>
            <h2 className="font-serif text-lg text-gold mb-2">{t("chef.about")}</h2>
            <p className="text-neutral-300 leading-relaxed mb-6">{chef.bio}</p>
            <p className="text-sm text-neutral-400 flex items-center gap-2 mb-2"><GraduationCap size={15} className="text-gold" /> {chef.qualification}</p>
            <p className="text-sm text-neutral-400 mb-8">{t("common.languages")}: {chef.languages?.join(", ")}</p>
            <div className="flex flex-wrap items-center gap-6">
              <p><span className="font-serif text-3xl text-gold">€{chef.price_per_guest}</span> <span className="text-sm text-neutral-500">{t("common.perGuest")}</span></p>
              <GoldButton data-testid="book-this-chef-btn" onClick={() => navigate(`/${lang}/book?chef=${chef.slug}`)}>
                {t("chef.bookThis")}
              </GoldButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* DISHES */}
      <section className="bg-coal border-y border-white/5 py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-8">
          <Reveal><h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-ivory mb-12">{t("chef.signature")}</h2></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {chef.dishes?.map((d, i) => (
              <Reveal key={d.id} delay={(i % 3) * 0.08}>
                <div className="group bg-ink border border-white/8 hover:border-gold/40 transition-colors" data-testid={`dish-card-${d.id}`}>
                  <div className="overflow-hidden aspect-[3/2]">
                    <img src={d.image} alt={localName(d, lang)} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-serif text-xl text-ivory">{d.name}</h3>
                      <span className="text-gold font-serif text-lg whitespace-nowrap">€{d.price_per_guest}</span>
                    </div>
                    <p className="text-sm text-neutral-400 mt-2 leading-relaxed">{localDesc(d, lang)}</p>
                    <p className="text-xs text-neutral-500 mt-4 flex items-center gap-1.5"><Clock size={11} /> {t("chef.prep", { n: d.prep_time_min })}</p>
                    {d.allergens?.length > 0 && <p className="text-xs text-neutral-600 mt-1">{t("chef.allergens")}: {d.allergens.join(", ")}</p>}
                    {d.dietary?.length > 0 && <p className="text-xs text-gold/60 mt-1">{d.dietary.join(" · ")}</p>}
                    <button data-testid={`book-dish-${d.id}`} onClick={() => navigate(`/${lang}/book?chef=${chef.slug}&dish=${d.id}`)}
                      className="mt-5 text-xs uppercase tracking-[0.2em] text-gold gold-underline">
                      {t("common.book")}
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="max-w-[900px] mx-auto px-4 sm:px-8 py-20 md:py-28" data-testid="chef-reviews-section">
        <Reveal><h2 className="font-serif text-2xl sm:text-3xl text-ivory mb-10">{t("chef.reviews")}</h2></Reveal>
        {(!chef.reviews || chef.reviews.length === 0) && <p className="text-neutral-500">{t("common.empty")}</p>}
        {chef.reviews?.map((r, i) => (
          <Reveal key={i}>
            <div className="border-b border-white/8 py-7">
              <div className="flex items-center justify-between mb-2">
                <p className="text-ivory font-medium">{r.customer_name}</p>
                <span className="flex items-center gap-1 text-gold text-sm"><Star size={13} className="fill-gold" /> {r.review?.overall}</span>
              </div>
              <p className="text-sm text-neutral-400 italic">"{r.review?.comment}"</p>
              <p className="text-xs text-neutral-600 mt-2">{r.date}</p>
            </div>
          </Reveal>
        ))}
        <div className="mt-12 text-center">
          <Link to={`/${lang}/chefs`} className="text-sm text-gold gold-underline">← {t("common.back")}</Link>
        </div>
      </section>
    </div>
  );
}
